import React from 'react';
import { ContextMenuState, TreeNode } from '../../types';
import { TreeView, GitStatusInfo } from '../../components/TreeView';

type ExplorerPanelProps = {
  filteredTree: TreeNode[];
  collapsed: Record<string, boolean>;
  activeFile: string | null;
  selectedNodeId: string | null;
  multiSelectedIds?: Set<string>;
  treeFilter: string;
  fileTypeFilter: string[];
  availableFileTypes: string[];
  showCreateMenu: boolean;
  contextMenu: ContextMenuState;
  isDirty: (path: string) => boolean;
  gitStatus?: Record<string, GitStatusInfo>;
  hasClipboard?: boolean;
  onFilterChange: (val: string) => void;
  onFileTypeFilterChange: (types: string[]) => void;
  onToggleCreateMenu: () => void;
  onCreateDirectory: () => void;
  onCreateFile: () => void;
  onAddProject: () => void;
  onCloseContextMenu: () => void;
  onContextMenu: (e: React.MouseEvent, node: TreeNode) => void;
  onSelectNode: (id: string, event?: React.MouseEvent) => void;
  onOpenFile: (id: string) => void;
  onToggleCollapse: (path: string) => void;
  onRename: () => void;
  onDelete: () => void;
  onNewFile: () => void;
  onNewFolder: () => void;
  onMove?: (sourceId: string, targetId: string) => void;
  onCopy?: () => void;
  onCut?: () => void;
  onPaste?: () => void;
  onDuplicate?: () => void;
  onToggleFavorite?: () => void;
  isFavorite?: boolean;
};

export const ExplorerPanel = React.memo(function ExplorerPanel({
  filteredTree,
  collapsed,
  activeFile,
  selectedNodeId,
  multiSelectedIds,
  treeFilter,
  fileTypeFilter,
  availableFileTypes,
  showCreateMenu,
  contextMenu,
  isDirty,
  gitStatus,
  hasClipboard,
  onFilterChange,
  onFileTypeFilterChange,
  onToggleCreateMenu,
  onCreateDirectory,
  onCreateFile,
  onAddProject,
  onCloseContextMenu,
  onContextMenu,
  onSelectNode,
  onOpenFile,
  onToggleCollapse,
  onRename,
  onDelete,
  onNewFile,
  onNewFolder,
  onMove,
  onCopy,
  onCut,
  onPaste,
  onDuplicate,
  onToggleFavorite,
  isFavorite,
}: ExplorerPanelProps) {
  const [showTypeFilter, setShowTypeFilter] = React.useState(false);

  const handleToggleFileType = (ext: string) => {
    if (fileTypeFilter.includes(ext)) {
      onFileTypeFilterChange(fileTypeFilter.filter(t => t !== ext));
    } else {
      onFileTypeFilterChange([...fileTypeFilter, ext]);
    }
  };

  const clearFileTypeFilter = () => {
    onFileTypeFilterChange([]);
  };
  return (
    <aside className="sidebar">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Explorer</p>
          <h2>Project files</h2>
        </div>
        <div className="panel-header-actions">
          <div className="panel-drag-handle" title="Panelleri sürükle">
            ⠿
          </div>
          <div className="menu-wrapper">
            <button className="micro" onClick={onToggleCreateMenu}>
              +
            </button>
            {showCreateMenu && (
              <div className="menu">
                <button className="menu-item" onClick={onCreateDirectory}>
                  Create a directory
                </button>
                <button className="menu-item" onClick={onCreateFile}>
                  Create a file
                </button>
                <button className="menu-item" onClick={onAddProject}>
                  Add project
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="tree-filter">
        <div className="tree-filter-row">
          <input type="text" placeholder="Filter files..." value={treeFilter} onChange={(e) => onFilterChange(e.target.value)} />
          <button
            className={`tree-filter-type-btn ${fileTypeFilter.length > 0 ? 'active' : ''}`}
            onClick={() => setShowTypeFilter(!showTypeFilter)}
            title="Filter by file type"
          >
            {fileTypeFilter.length > 0 ? `${fileTypeFilter.length}` : '✱'}
          </button>
        </div>
        {showTypeFilter && availableFileTypes.length > 0 && (
          <div className="tree-filter-types">
            <div className="tree-filter-types-header">
              <span className="tree-filter-types-title">File types</span>
              {fileTypeFilter.length > 0 && (
                <button className="tree-filter-types-clear" onClick={clearFileTypeFilter}>
                  Clear
                </button>
              )}
            </div>
            <div className="tree-filter-types-list">
              {availableFileTypes.map(ext => (
                <label key={ext} className="tree-filter-type-item">
                  <input
                    type="checkbox"
                    checked={fileTypeFilter.includes(ext)}
                    onChange={() => handleToggleFileType(ext)}
                  />
                  <span className="tree-filter-type-ext">.{ext}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="tree" onClick={onCloseContextMenu}>
        <TreeView
          tree={filteredTree}
          collapsed={collapsed}
          activeFile={activeFile}
          selectedNodeId={selectedNodeId}
          multiSelectedIds={multiSelectedIds}
          isDirty={isDirty}
          gitStatus={gitStatus}
          onSelect={onSelectNode}
          onOpen={onOpenFile}
          onToggle={onToggleCollapse}
          onContextMenu={onContextMenu}
          onMove={onMove}
        />
        {contextMenu.open && contextMenu.node && (
          <div className="context-menu" style={{ top: contextMenu.y, left: contextMenu.x }}>
            <button onClick={onCopy}>Copy</button>
            <button onClick={onCut}>Cut</button>
            {hasClipboard && <button onClick={onPaste}>Paste</button>}
            <button onClick={onDuplicate}>Duplicate</button>
            <button onClick={onToggleFavorite}>
              {isFavorite ? '★ Remove from Favorites' : '☆ Add to Favorites'}
            </button>
            <div className="context-menu-divider" />
            <button onClick={onRename}>Rename</button>
            <button onClick={onDelete}>Delete</button>
            <div className="context-menu-divider" />
            <button onClick={onNewFile}>New file</button>
            <button onClick={onNewFolder}>New folder</button>
          </div>
        )}
      </div>
      <div className="quick-actions">
        <p className="eyebrow">Shortcuts</p>
        <div className="shortcut-row">
          <span>Format document</span>
          <code>⇧⌘F</code>
        </div>
        <div className="shortcut-row">
          <span>Go to file</span>
          <code>⌘P</code>
        </div>
        <div className="shortcut-row">
          <span>Command palette</span>
          <code>⇧⌘P</code>
        </div>
        <div className="shortcut-row">
          <span>Toggle preview</span>
          <code>⌘K</code>
        </div>
      </div>
    </aside>
  );
});
